import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, Route, Car, ArrowUpRight, ArrowRight, ChevronDown, Check, X, Backpack } from "lucide-react";
import { waLink, tourMessage, trackWhatsApp } from "@/lib/whatsapp";
import { TOURS, WHAT_TO_BRING, formatBRL } from "@/lib/tours";
import { IMAGES } from "@/lib/images";
import Reveal from "./Reveal";
import WhatsAppIcon from "./WhatsAppIcon";

const chipClass =
  "rounded-full border border-white/10 bg-[#0D1117] px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-stone-300";

const TourImage = ({ tour }) => {
  const src = tour.image ? IMAGES[tour.image] : null;
  if (!src) return null;
  return (
    <div className="relative aspect-[16/10] overflow-hidden">
      <img
        src={src}
        alt={tour.name}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#161B22] via-transparent to-transparent" />
      <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full border border-white/20 bg-[#0D1117]/70 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-300 backdrop-blur-md">
        <Clock className="h-3 w-3" />
        {tour.duration}
      </span>
    </div>
  );
};

const TourDetails = ({ tour }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-5 border-t border-white/10 pt-4">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`tour-details-${tour.slug}`}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-widest text-stone-400 transition-colors duration-300 hover:text-amber-400"
      >
        Detalhes do passeio
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 flex flex-col gap-5">
          <div className="flex flex-wrap gap-2">
            <span className={chipClass}>Duração: {tour.duration}</span>
            <span className={chipClass}>Dificuldade: Fácil</span>
          </div>
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-400">
              Incluso
            </p>
            <ul className="mt-2.5 space-y-1.5">
              {tour.included.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-stone-300">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-500">
              Não incluso
            </p>
            <ul className="mt-2.5 space-y-1.5">
              {tour.notIncluded.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-stone-400">
                  <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-400">
              O que levar
            </p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {WHAT_TO_BRING.map((item) => (
                <span key={item} className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-xs text-stone-300">
                  <Backpack className="h-3 w-3 text-amber-400/70" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TourCard = ({ tour }) => (
  <article
    data-testid={`tour-card-${tour.slug}`}
    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#161B22] transition-[border-color,transform] duration-500 hover:-translate-y-1.5 hover:border-amber-400/40"
  >
    <TourImage tour={tour} />
    <div className="flex flex-1 flex-col p-6">
      <p className="mb-3 flex items-start gap-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.15em] text-amber-300/80">
        <Route className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {tour.stops.join(" → ")}
      </p>
      <h3 className="font-display text-lg font-bold leading-snug text-stone-50 sm:text-xl">
        {tour.name}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-stone-400">{tour.description}</p>
      <div className="mt-5 rounded-xl border border-amber-400/20 bg-[#0D1117] px-4 py-3">
        <p className="text-sm text-stone-300">
          A partir de{" "}
          <span className="font-display text-xl font-extrabold text-amber-400">
            R$ {formatBRL(tour.price)}
          </span>{" "}
          por pessoa
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500">
          Valor mínimo da reserva: R$ {formatBRL(tour.min)} ({tour.minNote})
        </p>
      </div>
      <div className="flex-1" />
      <a
        href={waLink(tourMessage(tour.name))}
        onClick={() => trackWhatsApp("card_passeio", tour.name)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`tour-cta-${tour.slug}`}
        className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:scale-[1.02] hover:bg-[#20BD5A]"
      >
        <WhatsAppIcon className="h-4 w-4" />
        Consultar e reservar
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </a>
      <Link
        to={`/${tour.slug}`}
        data-testid={`tour-link-${tour.slug}`}
        className="group/link mt-3 flex items-center justify-center gap-2 rounded-full border border-white/10 px-6 py-3 text-sm font-semibold text-stone-200 transition-[border-color,color] duration-300 hover:border-amber-400/50 hover:text-amber-400"
      >
        Ver passeio completo
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
      </Link>
      <TourDetails tour={tour} />
    </div>
  </article>
);

export default function Tours() {
  return (
    <section id="passeios" className="relative mx-auto max-w-7xl scroll-mt-24 px-5 py-24 sm:px-8 sm:py-32">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
            Catálogo de passeios
          </p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-extrabold leading-tight tracking-tight text-stone-50 sm:text-4xl lg:text-5xl">
            Escolha seu passeio{" "}
            <em className="font-accent font-medium italic text-amber-400">no Rio.</em>
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-stone-400 sm:text-base">
          Valores por pessoa, com ingressos incluídos. Cada passeio possui um valor
          mínimo de reserva. Experiências para pequenos grupos — consulte disponibilidade.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          data-testid="transfer-info"
          className="mt-8 flex flex-col gap-4 rounded-2xl border border-amber-400/25 bg-[#161B22] p-7 sm:flex-row sm:items-start sm:gap-6"
        >
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-amber-400/30 bg-[#0D1117]">
            <Car className="h-6 w-6 text-amber-400" />
          </div>
          <div>
            <p className="font-accent text-xl font-medium italic text-amber-400">
              Seu passeio começa e termina no seu hotel.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-stone-300 sm:text-base">
              <strong className="font-semibold text-stone-100">Transfer:</strong> buscamos
              você na hospedagem e deixamos no mesmo local ao final do passeio. Se preferir
              descer em outro ponto, combine pelo WhatsApp na hora da reserva: atendemos a
              pedido, sujeito à confirmação do guia e à rota dos demais participantes.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <p className="mt-6 max-w-3xl rounded-xl border border-white/10 bg-[#161B22] px-5 py-4 text-sm leading-relaxed text-stone-400">
          Reserva mediante sinal de confirmação. O passeio somente será considerado
          reservado após a confirmação do pagamento do sinal. O valor do sinal e as
          condições específicas são informados pelo WhatsApp de acordo com o valor total
          da reserva.
        </p>
      </Reveal>

      <div className="mt-14 grid items-start gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {TOURS.map((tour, i) => (
          <Reveal key={tour.slug} delay={Math.min(i, 2) * 0.1} className="h-full">
            <TourCard tour={tour} />
          </Reveal>
        ))}
      </div>
    </section>
  );
}
